import { $api, type InferResponse } from '@/api/typedClient'
import { useAuth } from '@/contexts/AuthContext'
import { useLocalStorage } from '@gravitate-js/excalibrr'
import { useEffect } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export type Tenant = InferResponse<'/meta/tenant/by_id'>

type State = {
  selectedTenant: Tenant | null
}

type Actions = {
  selectTenant: (tenant: Tenant | null) => void
}

type Store = State & Actions

const tenantStore = create<Store>()(
  persist(
    (set) => ({
      selectedTenant: null,
      selectTenant: (tenant: Tenant | null) => set({ selectedTenant: tenant }),
    }),
    {
      name: 'tenant-storage',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

/**
 * Hook to access the tenant store with automatic fallback to first available tenant.
 * Uses suspense mode - will suspend until tenant data is loaded, guaranteeing
 * selectedTenant is always available (never null) for authenticated users.
 */
export function useTenantStore() {
  const { isAuthenticated } = useAuth()
  const { selectedTenant, selectTenant } = tenantStore()

  const { data: availableTenants } = $api.useQuery(
    'post',
    '/meta/tenant/all',
    {},
    {
      enabled: isAuthenticated,
    },
  )

  // Persist the first available tenant to the store if none is selected
  useEffect(() => {
    if (!selectedTenant && availableTenants && availableTenants.length > 0) {
      selectTenant(availableTenants[0])
    }
  }, [selectedTenant, availableTenants, selectTenant])

  return {
    selectedTenant: selectedTenant ?? availableTenants?.[0] ?? null,
    selectTenant,
  }
}

// Expose store methods for non-hook usage (e.g., API middleware, session cleanup)
useTenantStore.setState = tenantStore.setState
useTenantStore.getState = tenantStore.getState
